import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  Alert,
  Divider,
  InputAdornment,
  Chip,
  Tooltip
} from '@mui/material';
import {
  Save as SaveIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Search as SearchIcon,
  QrCodeScanner as QrCodeScannerIcon,
  ArrowBack as ArrowBackIcon,
  FileUpload as FileUploadIcon,
  FileDownload as FileDownloadIcon
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import {
  getInventoryCountEntry,
  updateInventoryCountEntry,
  searchProductVariantsByBarcode,
  searchProductVariantsByBarcodes,
  searchProductVariants
} from '../../services/inventoryCountService';
import { InventoryCountEntry, InventoryCountEntryItem } from '../../types/inventoryCount';
import InventoryProductSearch from './InventoryProductSearch';

// نوع عنصر الإدخال في النموذج
interface EntryItem {
  id: string; // معرف مؤقت للعنصر في النموذج
  productId: string;
  productName: string;
  variantId: string;
  variantAttributes?: Record<string, string>;
  barcode?: string;
  quantity: number;
  unitId: string;
  unitName: string;
}

// نوع نتيجة البحث عن متغير المنتج
interface ProductVariantSearchResult {
  productId: string;
  productName: string;
  variantId: string;
  variantAttributes?: Record<string, string>;
  barcode?: string;
  unitId: string;
  unitName: string;
}

const EditInventoryCountEntry: React.FC = () => {
  const { entryId } = useParams<{ entryId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);
  const [entry, setEntry] = useState<InventoryCountEntry | null>(null);
  const [items, setItems] = useState<EntryItem[]>([]);
  const [notes, setNotes] = useState<string>('');
  const [barcode, setBarcode] = useState<string>('');
  const [isScanning, setIsScanning] = useState<boolean>(false);
  const [openAdvancedSearchDialog, setOpenAdvancedSearchDialog] = useState<boolean>(false);
  const [searchResults, setSearchResults] = useState<ProductVariantSearchResult[]>([]);
  const [searchLoading, setSearchLoading] = useState<boolean>(false);

  // حالة استيراد الإكسل
  const [isImporting, setIsImporting] = useState<boolean>(false);
  const [importErrors, setImportErrors] = useState<string[]>([]);

  // مرجع لحقل إدخال الباركود
  const barcodeInputRef = useRef<HTMLInputElement>(null);
  
  // جلب تفاصيل أمر الإدخال
  useEffect(() => {
    const fetchEntryDetails = async () => {
      if (!entryId) return;
      
      setLoading(true);
      try {
        const response = await getInventoryCountEntry(entryId);
        if (response.success) {
          setEntry(response.entry);
          setNotes(response.entry.notes || '');
          
          // تحويل العناصر إلى الصيغة المطلوبة للنموذج
          const formattedItems: EntryItem[] = response.entry.items.map(item => {
            // تسجيل البيانات للتشخيص
            console.log('بيانات العنصر من الخادم:', {
              productName: item.productName,
              unitId: item.unitId,
              unitName: item.unitName,
              unit: item.unit
            });
            
            // إذا كان item.unit موجود ويحتوي على _id، نستخدمه بدلاً من unitId
            const correctUnitId = (item.unit && item.unit._id) ? item.unit._id : item.unitId;
            
            return {
              id: `existing-${item.productId}-${item.variantId}`,
              productId: item.productId,
              productName: item.productName,
              variantId: item.variantId,
              variantAttributes: item.variantAttributes,
              barcode: item.barcode,
              quantity: item.quantity,
              unitId: correctUnitId,
              unitName: item.unitName
            };
          });
          
          setItems(formattedItems);
        } else {
          setError('حدث خطأ أثناء جلب بيانات أمر الإدخال');
        }
      } catch (err) {
        console.error('Error fetching inventory count entry details:', err);
        setError('حدث خطأ أثناء الاتصال بالخادم');
      } finally {
        setLoading(false);
      }
    };

    fetchEntryDetails();
  }, [entryId]);

  // التركيز على حقل إدخال الباركود عند تفعيل المسح الضوئي
  useEffect(() => {
    if (isScanning && barcodeInputRef.current) {
      barcodeInputRef.current.focus();
    }
  }, [isScanning]);

  // إنشاء معرف فريد مؤقت
  const generateTempId = () => `temp-${Date.now()}-${Math.floor(Math.random() * 1000)}`;

  // إضافة عنصر جديد إلى القائمة
  const addItem = (item: Omit<EntryItem, 'id'>) => {
    const newItem: EntryItem = {
      ...item,
      id: generateTempId()
    };
    
    setItems(prevItems => {
      // التحقق مما إذا كان العنصر موجودًا بالفعل
      const existingItemIndex = prevItems.findIndex(i => i.productId === newItem.productId && i.variantId === newItem.variantId);
      
      if (existingItemIndex >= 0) {
        // تحديث الكمية إذا كان العنصر موجودًا بالفعل
        const updatedItems = [...prevItems];
        updatedItems[existingItemIndex] = {
          ...updatedItems[existingItemIndex],
          quantity: updatedItems[existingItemIndex].quantity + newItem.quantity
        };
        return updatedItems;
      } else {
        // إضافة عنصر جديد
        return [...prevItems, newItem];
      }
    });
  };

  // حذف عنصر من القائمة
  const removeItem = (id: string) => {
    setItems(items.filter(item => item.id !== id));
  };

  // تغيير كمية عنصر
  const updateItemQuantity = (id: string, quantity: number) => {
    setItems(items.map(item => 
      item.id === id ? { ...item, quantity } : item
    ));
  };

  // البحث عن متغير المنتج بواسطة الباركود
  const handleBarcodeSearch = async (barcode: string) => {
    if (!barcode) return;
    
    try {
      const response = await searchProductVariantsByBarcode(barcode);
      if (response.success && response.variant) {
        // إضافة العنصر إلى القائمة
        addItem({
          productId: response.variant.productId,
          productName: response.variant.productName,
          variantId: response.variant.variantId,
          variantAttributes: response.variant.variantAttributes,
          barcode: response.variant.barcode,
          quantity: 1, // كمية افتراضية
          unitId: response.variant.unitId,
          unitName: response.variant.unitName
        });
        
        // مسح حقل الباركود
        setBarcode('');
      } else {
        setError('لم يتم العثور على منتج بهذا الباركود');
        setTimeout(() => setError(null), 3000);
      }
    } catch (err) {
      console.error('Error searching product by barcode:', err);
      setError('حدث خطأ أثناء البحث عن المنتج');
      setTimeout(() => setError(null), 3000);
    }
  };


  // اختيار متغير منتج من نتائج البحث
  const handleSelectVariant = (variant: ProductVariantSearchResult) => {
    addItem({
      productId: variant.productId,
      productName: variant.productName,
      variantId: variant.variantId,
      variantAttributes: variant.variantAttributes,
      barcode: variant.barcode,
      quantity: 1, // كمية افتراضية
      unitId: variant.unitId,
      unitName: variant.unitName
    });
    
    setSearchResults([]);
  };

  // معالج اختيار متغير واحد من نافذة البحث المتقدمة
  const handleAdvancedSearchVariantSelect = (variant: any) => {
    addItem({
      productId: variant.productId,
      productName: variant.productName,
      variantId: variant.variantId,
      variantAttributes: variant.variantAttributes,
      barcode: variant.barcode,
      quantity: 1, // كمية افتراضية
      unitId: variant.unitId,
      unitName: variant.unitName
    });
    
    setOpenAdvancedSearchDialog(false);
  };

  // معالج اختيار عدة متغيرات من نافذة البحث المتقدمة
  const handleAdvancedSearchVariantsSelect = (variants: any[]) => {
    variants.forEach(variant => {
      addItem({
        productId: variant.productId,
        productName: variant.productName,
        variantId: variant.variantId,
        variantAttributes: variant.variantAttributes,
        barcode: variant.barcode,
        quantity: 1, // كمية افتراضية
        unitId: variant.unitId,
        unitName: variant.unitName
      });
    });
    
    setOpenAdvancedSearchDialog(false);
    setSuccess(true);
    setTimeout(() => setSuccess(false), 3000);
  };


  // تنفيذ البحث بالباركود عند الضغط على Enter
  const handleBarcodeKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault(); // منع السلوك الافتراضي للـ form submit
      handleBarcodeSearch(barcode);
    }
  };

  // تصدير قالب الإكسل
  const handleExportTemplate = () => {
    const templateData = [
      { 'الباركود': 'مثال: 1234567890123', 'الكمية': 'مثال: 10' },
      { 'الباركود': '', 'الكمية': '' }
    ];

    const ws = XLSX.utils.json_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'قالب استيراد المنتجات');

    // تعيين عرض الأعمدة
    ws['!cols'] = [
      { wch: 20 }, // عمود الباركود
      { wch: 15 }  // عمود الكمية
    ];

    XLSX.writeFile(wb, 'قالب_استيراد_منتجات_الجرد.xlsx');
  };

  // استيراد المنتجات من الإكسل
  const handleImportExcel = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsImporting(true);
    setImportErrors([]);

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      const errors: string[] = [];
      const newItems: EntryItem[] = [];

      // تجميع الصفوف وتوحيد الباركود المكرر داخل نفس الملف
      interface ParsedCountRow { rowNumber: number; barcode: string; quantity: number; }
      const parsedRows: ParsedCountRow[] = [];
      const seenBarcodes = new Map<string, ParsedCountRow>();

      for (let i = 0; i < jsonData.length; i++) {
        const row = jsonData[i] as any;
        const rowNumber = i + 2; // +2 لأن الصف الأول هو العناوين والفهرسة تبدأ من 1

        const barcode = row['الباركود']?.toString().trim();
        const quantity = parseInt(row['الكمية']?.toString()) || 0;

        if (!barcode) {
          errors.push(`الصف ${rowNumber}: الباركود مطلوب`);
          continue;
        }

        if (quantity <= 0) {
          errors.push(`الصف ${rowNumber}: الكمية يجب أن تكون أكبر من صفر`);
          continue;
        }

        // دمج الكميات عند تكرار نفس الباركود داخل الملف
        const key = barcode.toLowerCase();
        const existingRow = seenBarcodes.get(key);
        if (existingRow) {
          existingRow.quantity += quantity;
          continue;
        }

        const parsedRow: ParsedCountRow = { rowNumber, barcode, quantity };
        seenBarcodes.set(key, parsedRow);
        parsedRows.push(parsedRow);
      }

      // بحث مجمّع واحد لكل الباركودات بدل طلب لكل صف
      const barcodeList = parsedRows.map(r => r.barcode);
      const barcodeResults = await searchProductVariantsByBarcodes(barcodeList);

      for (const parsedRow of parsedRows) {
        const match = barcodeResults.get(parsedRow.barcode.toLowerCase());

        if (!match || !match.found) {
          errors.push(`الصف ${parsedRow.rowNumber}: لم يتم العثور على منتج بالباركود ${parsedRow.barcode}`);
          continue;
        }

        // التحقق من عدم وجود المنتج مسبقاً في الصفحة
        const existingItem = items.find(item =>
          item.productId === match.productId &&
          item.variantId === match.variantId
        );

        if (existingItem) {
          errors.push(`الصف ${parsedRow.rowNumber}: المنتج موجود مسبقاً (${match.productName})`);
          continue;
        }

        // دمج الكميات إذا أشار باركودان مختلفان لنفس المنتج/المتغير داخل نفس الاستيراد
        const existingNewItem = newItems.find(item =>
          item.productId === match.productId &&
          item.variantId === match.variantId
        );

        if (existingNewItem) {
          existingNewItem.quantity += parsedRow.quantity;
          continue;
        }

        // الحفاظ على دلالة unitId كما كانت (اسم الوحدة)
        const resolvedUnitName = match.productUnitName || match.unitName || 'قطعة';

        newItems.push({
          id: `import-${Date.now()}-${newItems.length}`,
          productId: match.productId,
          productName: match.productName,
          variantId: match.variantId,
          variantAttributes: match.variantAttributes || {},
          barcode: match.mainBarcode || parsedRow.barcode,
          unitId: resolvedUnitName,
          unitName: resolvedUnitName,
          quantity: parsedRow.quantity
        });
      }

      // إضافة العناصر الجديدة
      if (newItems.length > 0) {
        setItems(prevItems => [...prevItems, ...newItems]);
      }

      setImportErrors(errors);

      if (errors.length === 0) {
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
      }

    } catch (error) {
      console.error('خطأ في استيراد الملف:', error);
      setImportErrors(['خطأ في قراءة الملف. تأكد من أن الملف بصيغة Excel صحيحة.']);
    } finally {
      setIsImporting(false);
      // إعادة تعيين قيمة input لتمكين استيراد نفس الملف مرة أخرى
      event.target.value = '';
    }
  };

  // تقديم النموذج وحفظ أمر الإدخال
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // التحقق من وجود عناصر
    if (items.length === 0) {
      setError('يجب إضافة عنصر واحد على الأقل');
      return;
    }

    if (!entry || !entryId) return;
    
    setSubmitting(true);
    setError(null);
    
    try {
      // تحويل العناصر إلى الصيغة المطلوبة للواجهة البرمجية
      const entryItems = items.map(item => ({
        productId: item.productId,
        variantId: item.variantId,
        quantity: item.quantity,
        unitId: item.unitId,
        unitName: item.unitName,
        barcode: item.barcode,
        productName: item.productName,
        variantAttributes: item.variantAttributes
      }));
      
      const response = await updateInventoryCountEntry(entryId, {
        requestId: entry.requestId,
        items: entryItems,
        notes: notes.trim() || undefined
      });

      if (response && response.success) {
        setSuccess(true);
        // الانتقال إلى صفحة تفاصيل أمر الإدخال بعد التحديث بنجاح
        setTimeout(() => {
          navigate(`/inventory-count/entries/${entryId}`);
        }, 1500);
      } else {
        setError('حدث خطأ أثناء تحديث أمر الإدخال');
      }
    } catch (err) {
      console.error('Error updating inventory count entry:', err);
      setError('حدث خطأ أثناء الاتصال بالخادم');
    } finally {
      setSubmitting(false);
    }
  };

  // عرض حالة التحميل
  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 5 }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          جاري تحميل بيانات أمر الإدخال...
        </Typography>
      </Box>
    );
  }

  // عرض رسالة في حالة عدم وجود أمر إدخال
  if (!entry) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        لم يتم العثور على أمر الإدخال المطلوب
      </Alert>
    );
  }

  // عرض سمات المتغير كنص
  const formatVariantAttributes = (attributes?: Record<string, string>) => {
    if (!attributes) return '';
    
    return Object.entries(attributes)
      .map(([key, value]) => `${key}: ${value}`)
      .join(', ');
  };

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate>
      {/* رسالة النجاح */}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          تم تحديث أمر الإدخال بنجاح. جاري الانتقال إلى صفحة التفاصيل...
        </Alert>
      )}

      {/* رسالة الخطأ */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* أخطاء الاستيراد */}
      {importErrors.length > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            تم العثور على أخطاء أثناء الاستيراد:
          </Typography>
          <Box component="ul" sx={{ m: 0, pl: 2 }}>
            {importErrors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </Box>
        </Alert>
      )}

      {/* معلومات أمر الإدخال */}
      <Paper
        elevation={2}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 2,
          backgroundColor: 'background.paper'
        }}
      >
        <Typography variant="h6" gutterBottom>
          تعديل أمر الإدخال - {entry.entryNumber}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          تاريخ الإنشاء: {new Date(entry.createdAt).toLocaleString('ar-SA')}
        </Typography>
      </Paper>

      {/* أدوات البحث والإدخال */}
      <Paper
        elevation={3}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 2,
          backgroundColor: 'background.paper'
        }}
      >
        <Typography variant="subtitle1" gutterBottom fontWeight="bold">
          إضافة المنتجات
        </Typography>
        <Divider sx={{ mb: 2 }} />

        <Grid container spacing={2} alignItems="center">
          {/* مسح الباركود */}
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField
                label="مسح الباركود"
                fullWidth
                value={barcode}
                onChange={(e) => setBarcode(e.target.value)}
                onKeyPress={handleBarcodeKeyPress}
                inputRef={barcodeInputRef}
                disabled={!isScanning}
                placeholder={isScanning ? "قم بمسح الباركود..." : "انقر على زر المسح لتفعيل الماسح"}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title={isScanning ? "إيقاف المسح" : "تفعيل المسح"}>
                        <IconButton
                          onClick={() => setIsScanning(!isScanning)}
                          color={isScanning ? "primary" : "default"}
                        >
                          <QrCodeScannerIcon />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Grid>

          {/* البحث عن المنتجات */}
          <Grid item xs={12} md={6}>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<SearchIcon />}
              onClick={() => setOpenAdvancedSearchDialog(true)}
              fullWidth
              sx={{ height: '56px' }}
            >
              البحث المتقدم عن المنتجات
            </Button>
          </Grid>

          {/* أزرار الإكسل */}
          <Grid item xs={12} md={3}>
            <Button
              variant="outlined"
              color="info"
              startIcon={<FileDownloadIcon />}
              onClick={handleExportTemplate}
              fullWidth
              sx={{ height: '56px' }}
            >
              تحميل قالب الإكسل
            </Button>
          </Grid>

          <Grid item xs={12} md={3}>
            <Button
              variant="outlined"
              color="success"
              startIcon={<FileUploadIcon />}
              component="label"
              disabled={isImporting}
              fullWidth
              sx={{ height: '56px' }}
            >
              {isImporting ? 'جاري الاستيراد...' : 'استيراد من الإكسل'}
              <input
                type="file"
                hidden
                accept=".xlsx,.xls"
                onChange={handleImportExcel}
              />
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* قائمة المنتجات المضافة */}
      <Paper
        elevation={3}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 2,
          backgroundColor: 'background.paper'
        }}
      >
        <Typography variant="subtitle1" gutterBottom fontWeight="bold">
          المنتجات المضافة
        </Typography>
        <Divider sx={{ mb: 2 }} />

        {items.length === 0 ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            لم تتم إضافة أي منتجات بعد. استخدم الماسح الضوئي أو ابحث عن المنتجات لإضافتها.
          </Alert>
        ) : (
          <>
            {/* Total Quantities Summary */}
            <Box sx={{ p: 2, bgcolor: 'primary.main', color: 'primary.contrastText', mb: 2, borderRadius: 1 }}>
              <Typography variant="h6" sx={{ textAlign: 'center', fontWeight: 'bold' }}>
                إجمالي الكميات: {items.reduce((total, item) => total + Math.abs(item.quantity || 0), 0)} قطعة
              </Typography>
            </Box>
            <TableContainer sx={{ mb: 2 }}>
              <Table>
              <TableHead>
                <TableRow>
                  <TableCell>اسم المنتج</TableCell>
                  <TableCell>متغير المنتج</TableCell>
                  <TableCell>الباركود</TableCell>
                  <TableCell>الوحدة</TableCell>
                  <TableCell>الكمية</TableCell>
                  <TableCell>الإجراءات</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>{item.productName}</TableCell>
                    <TableCell>
                      {formatVariantAttributes(item.variantAttributes) || '-'}
                    </TableCell>
                    <TableCell>{item.barcode || '-'}</TableCell>
                    <TableCell>{item.unitName}</TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        value={item.quantity}
                        onChange={(e) => {
                          const value = Math.max(1, parseInt(e.target.value) || 0);
                          updateItemQuantity(item.id, value);
                        }}
                        size="small"
                        InputProps={{ inputProps: { min: 1 } }}
                        sx={{ width: '80px' }}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        color="error"
                        size="small"
                        onClick={() => removeItem(item.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </TableContainer>
          </>
        )}

        {/* إجمالي العناصر */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
          <Chip
            label={`إجمالي العناصر: ${items.length}`}
            color="primary"
            variant="outlined"
          />
        </Box>
      </Paper>

      {/* ملاحظات */}
      <Paper
        elevation={3}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 2,
          backgroundColor: 'background.paper'
        }}
      >
        <Typography variant="subtitle1" gutterBottom fontWeight="bold">
          ملاحظات
        </Typography>
        <Divider sx={{ mb: 2 }} />

        <TextField
          label="ملاحظات الإدخال"
          multiline
          rows={3}
          fullWidth
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </Paper>

      {/* أزرار الإجراءات */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/inventory-count/entries/${entryId}`)}
        >
          إلغاء
        </Button>
        
        <Button
          type="submit"
          variant="contained"
          color="primary"
          startIcon={submitting ? <CircularProgress size={20} /> : <SaveIcon />}
          disabled={submitting || items.length === 0}
        >
          {submitting ? 'جاري الحفظ...' : 'حفظ التغييرات'}
        </Button>
      </Box>


      {/* نافذة البحث المتقدمة عن المنتجات */}
      <InventoryProductSearch
        open={openAdvancedSearchDialog}
        onClose={() => setOpenAdvancedSearchDialog(false)}
        onSelectVariant={handleAdvancedSearchVariantSelect}
        onSelectVariants={handleAdvancedSearchVariantsSelect}
        multiSelect
        warehouseId={(entry as any)?.warehouse?._id || (entry as any)?.warehouse}
      />
    </Box>
  );
};

export default EditInventoryCountEntry;